import React, { useCallback, useRef } from 'react';

import FormContext from './Context';
import createFormStore from './Store';

function FormProvider({
  children,
  initialValues,
  validators,
  handleClose: handleClosePos,
  handleSalvar: handleSalvarPos,
  config,
  isDialogoConfirmacaoActions = true,
  ConfirmacaoComponent,
  getMensagemConfirmacao,
}) {
  const storeRef = useRef(createFormStore(initialValues, validators, config));
  const refCache = useRef({});
  const refDialogoConfirmacao = useRef(null);

  const focusNext = (currentName) => {
    const store = storeRef.current;
    const currentField = store.getField(currentName);

    if (!currentField) return;

    const currentGroup = currentField.group;

    const fields = store.getFields();

    const fieldOrder = store.getFieldOrder();

    const groupFields = fieldOrder
      .map((name) => ({ name, ...fields[name] }))
      .filter((f) => f && f.group === currentGroup);

    if (!groupFields.length) return;

    groupFields.sort((a, b) => {
      const pa = a.priority ?? 100;
      const pb = b.priority ?? 100;
      return pa - pb;
    });

    const currentIndex = groupFields.findIndex((f) => f.name === currentName);

    if (currentIndex === -1) return;

    const total = groupFields.length;

    for (let step = 1; step <= total; step++) {
      const nextIndex = (currentIndex + step) % total;
      const nextField = groupFields[nextIndex];

      if (!nextField) continue;
      if (nextField.disabled) continue;

      if (nextField.el?.focus) {
        nextField.el.focus();
        nextField.el.select?.();
      }

      return;
    }
  };

  const isFocusable = (f) => f && !f.disabled && f.el;

  const focusNextGroup = (currentName, reverse = false) => {
    const store = storeRef.current;
    const fields = store.getFields();

    const currentField = fields[currentName];

    if (!currentField) return;

    const groups = [];

    const fieldOrder = store.getFieldOrder();

    fieldOrder.forEach((name) => {
      const g = fields[name]?.group ?? 'default';
      if (!groups.includes(g)) {
        groups.push(g);
      }
    });

    const currentGroup = currentField.group ?? 'default';

    const currentGroupIndex = groups.indexOf(currentGroup);

    if (currentGroupIndex === -1) return;

    const totalGroups = groups.length;

    for (let step = 1; step <= totalGroups; step++) {
      const nextGroupIndex = reverse
        ? (currentGroupIndex - step + totalGroups) % totalGroups
        : (currentGroupIndex + step) % totalGroups;

      const nextGroup = groups[nextGroupIndex];

      const groupFields = fieldOrder
        .map((name) => ({ name, ...fields[name] }))
        .filter((f) => f.group === nextGroup && isFocusable(f))
        .sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));

      if (groupFields.length) {
        const next = groupFields[0];

        next.el.focus();
        next.el.select?.();
      }
      break;
    }
  };

  const getStableRef = (name, identificador) => {
    if (refCache.current[name]) {
      return refCache.current[name];
    }

    const fn = (el) => {
      const store = storeRef.current;

      if (!el) {
        unregisterField(name);
        return;
      }

      store.registerField(name, el, identificador);
    };

    refCache.current[name] = fn;

    return fn;
  };

  const unregisterField = (name) => {
    const store = storeRef.current;
    store.unregisterField(name);
  };

  const handleSubmitPos = () => {
    const store = storeRef.current;
    const data = store.getValues();
    handleSalvarPos(data);
  };

  const handleSubmit = async () => {
    if (!formState.isError) {
      if (isDialogoConfirmacaoActions) {
        // Alguns formulários (ex: redimensionar mapa) precisam de uma
        // mensagem de confirmação dinâmica, calculada a partir dos dados
        // atuais do form (ex: quantos endereços seriam desvinculados).
        const mensagem = getMensagemConfirmacao
          ? await getMensagemConfirmacao(storeRef.current.getValues())
          : 'Deseja realmente finalizar?';
        openDialogo('Finalizar', mensagem, 1);
      } else {
        handleSubmitPos();
      }
    }
  };

  const handleClose = () => {
    if (isDialogoConfirmacaoActions) {
      openDialogo('Cancelar', 'Deseja realmente cancelar?', 2);
    } else {
      handleClosePos();
    }
  };

  function getValue(name) {
    const store = storeRef.current;
    return store.getValue(name);
  }

  function setValue(name, value) {
    const store = storeRef.current;
    store.setValue(name, value);
  }

  const register = useCallback(
    (name, identificador) => ({
      name,
      ref: getStableRef(name, identificador),
    }),
    [],
  );

  function setFieldState(name, state) {
    const store = storeRef.current;
    store.setFieldState(name, state);
  }

  const formState = storeRef.current.getFormState();
  const fields = storeRef.current.getFields();

  function subscribeField(name, cb) {
    const store = storeRef.current;
    return store.subscribeField(name, cb);
  }

  function subscribeFormState(cb) {
    const store = storeRef.current;
    return store.subscribeFormState(cb);
  }

  function registerEffect(deps, callback) {
    const store = storeRef.current;
    return store.registerEffect(deps, callback);
  }

  const value = {
    register,
    formState,
    handleSubmit,
    handleClose,
    setFieldState,
    getValue,
    setValue,
    focusNext,
    focusNextGroup,
    subscribeField,
    subscribeFormState,
    registerEffect,
    fields,
  };

  async function handleConfirma(record) {
    switch (record) {
      case 1:
        handleSubmitPos();
        break;
      case 2:
        handleClosePos();
        break;
      default:
        break;
    }
  }

  function openDialogo(title, content, code) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(title, content, code);
    }
  }

  return (
    <>
      <FormContext.Provider value={value}>{children}</FormContext.Provider>
      <ConfirmacaoComponent
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
    </>
  );
}

export default FormProvider;
